import type {TriggerIntegrationConfigComponentProps} from '@cohort/merchants/apps';
import Input from '@cohort/merchants/components/form/input/Input';
import type {GarminConnectCompleteActivityTriggerStruct} from '@cohort/shared/apps/garmin-connect/triggers/completeActivity';
import {toMeters, toMetersPerSecond, toSeconds} from '@cohort/shared/utils/convert';
import {useController} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

export type CompleteActivityMetricsFormMetric = {
  hasPropertyName: `trigger.triggerIntegrationConfig.has${string}`;
  propertyName: `trigger.triggerIntegrationConfig.${string}`;
  convertionMethod?: (value: number) => number;
  convertedPropertyName?: `trigger.triggerIntegrationConfig.${string}`;
  showPropertyLabel: string;
  label: string;
  description: string;
};

const GarminConnectCompleteActivityMetricsForm: React.FC<
  TriggerIntegrationConfigComponentProps<GarminConnectCompleteActivityTriggerStruct>
> = ({formReturn: {register, control, setValue}}) => {
  const {t} = useTranslation('app-garmin-connect', {
    keyPrefix: 'triggerIntegrations.complete-activity.configComponent',
  });

  const metrics: CompleteActivityMetricsFormMetric[] = [
    {
      hasPropertyName: 'trigger.triggerIntegrationConfig.hasDistance',
      propertyName: 'trigger.triggerIntegrationConfig.minDistanceKilometers',
      convertionMethod: toMeters,
      convertedPropertyName: 'trigger.triggerIntegrationConfig.minDistanceMeters',
      showPropertyLabel: t('labelShowDistance'),
      label: t('labelActivityDistance'),
      description: t('descriptionInKilometers'),
    },
    {
      hasPropertyName: 'trigger.triggerIntegrationConfig.hasAverageElevationGain',
      propertyName: 'trigger.triggerIntegrationConfig.minAverageElevationGainMeters',
      showPropertyLabel: t('labelShowAverageElevationGain'),
      label: t('labelActivityAverageElevationGain'),
      description: t('descriptionInMeters'),
    },
    {
      hasPropertyName: 'trigger.triggerIntegrationConfig.hasAverageSpeed',
      propertyName: 'trigger.triggerIntegrationConfig.minAverageSpeedKilometersPerHour',
      convertionMethod: toMetersPerSecond,
      convertedPropertyName: 'trigger.triggerIntegrationConfig.minAverageSpeedMetersPerSecond',
      showPropertyLabel: t('labelShowAverageSpeed'),
      label: t('labelActivityAverageSpeed'),
      description: t('descriptionInKilometerPerHour'),
    },
    {
      hasPropertyName: 'trigger.triggerIntegrationConfig.hasDuration',
      propertyName: 'trigger.triggerIntegrationConfig.minDurationMinutes',
      convertionMethod: toSeconds,
      convertedPropertyName: 'trigger.triggerIntegrationConfig.minDurationSeconds',
      showPropertyLabel: t('labelShowDuration'),
      label: t('labelActivityDuration'),
      description: t('descriptionInMinutes'),
    },
  ];

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const fields = metrics.map(metric => useController({name: metric.hasPropertyName, control}));

  return (
    <div>
      <div className="flex flex-col gap-4">
        {metrics.map((metric, index) => {
          const hasMetric = fields[index];
          return (
            <div key={metric.hasPropertyName} className="flex flex-col gap-2">
              <Input
                name={metric.hasPropertyName}
                label={metric.showPropertyLabel}
                register={register}
                control={control}
                type="checkbox"
                labelPosition="right"
                onChange={e => {
                  setValue(metric.hasPropertyName, e.target.checked);
                  if (!e.target.checked) {
                    setValue(metric.propertyName, null);
                    if (metric.convertionMethod && metric.convertedPropertyName) {
                      setValue(metric.convertedPropertyName, null);
                    }
                  }
                }}
              />
              {hasMetric?.field.value && (
                <Input
                  name={metric.propertyName}
                  label={metric.label}
                  type="number"
                  placeholder="0"
                  description={metric.description}
                  register={register}
                  control={control}
                  min={0}
                  onChange={e => {
                    setValue(metric.propertyName, Number(e.target.value));
                    if (metric.convertionMethod && metric.convertedPropertyName) {
                      setValue(
                        metric.convertedPropertyName,
                        metric.convertionMethod(Number(e.target.value))
                      );
                    }
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GarminConnectCompleteActivityMetricsForm;
